import { defineStore } from "pinia";
import { useRecaptchaStore } from "@/domain/search/application/Recaptcha.store";
import { BestSeatStoreState } from "@/domain/ticket-selection/entities/ticket-selection/BestSeatStoreState.interface";
import {
    ApplicationTicketMaximumProvider,
    CurrencyAdditionalOptions,
    HoldState,
    PriceLevelState,
    PriceLevelStateCollectionFactory,
    PriceLevelStateCollectionProvider,
    PriceLevelStateProvider,
    TicketSelectionApplicationData,
    TicketSelectionConfiguration,
    TicketTypeState,
} from "@/domain/ticket-selection";
import { HoldStateProvider } from "@/domain/ticket-selection/data/providers/HoldState.provider";
import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";
import { Promotion } from "webshop-api";
import {
    AnalyticsEventName,
    AnalyticsStorage,
    FbqUtil,
    GTMTicketItemFactory,
    GTMUtils,
    TicketItem,
    Url
} from "webshop-modules";
import { Store } from "@/common/stores.enum";
import { AnalyticsEcommerceEvent } from '../../../../../modules';
import { CheckoutData } from '../../../../../modules/google-tag-manager/entity/base/CheckoutData.interface';

export const useBestSeatStore = defineStore(Store.BestSeat, {

    state: (): BestSeatStoreState => ({
        actionParams: null,
        actionUrl: null,
        currency: 'EUR',
        currencyOptions: null,
        maxTickets: 0,
        priceLevels: [],
        fees: [],
    }),

    getters: {
        activeHold(state: BestSeatStoreState): HoldState | undefined {
            const priceLevel = state.priceLevels.find((item: PriceLevelState) => item.active);

            return !priceLevel ? undefined : PriceLevelStateProvider.getActiveHoldFor(priceLevel);
        },
    },
    actions: {
        getActionParams(data: TicketSelectionConfiguration) {
            if (!data) {
                return null;
            }

            return Object.entries(data)
                .filter(([key]) => key !== 'actionPath')
                .filter(([key]) => key !== 'reCaptcha')
                .filter(([key]) => key !== 'cartCurrency')
                .filter(([key]) => key !== 'poolCurrency')
                .filter(([key]) => key !== 'currency')
                .filter(([key]) => key !== 'staticPath')
                .reduce((pre: string | null, cur) => {
                    const reducedCur = cur.reduce((prev, curr) => `${prev}=${curr}`);
                    return !pre ? reducedCur : `${pre}&${reducedCur}`;
                }, null);
        },
        getActionUrl(data: TicketSelectionConfiguration) {
            return data.actionPath;
        },
        setCurrency(value: string) {
            this.currency = value;
        },
        setCurrencyOptions(value: CurrencyAdditionalOptions) {
            this.currencyOptions = value;
        },
        setMaxTickets(value: number) {
            this.maxTickets = value;
        },
        setPriceLevelState(state: PriceLevelState[]) {
            this.priceLevels = state;
        },
        async initializeStore(data: TicketSelectionApplicationData) {
            if (!data.priceList) {
                return;
            }
            const applicationTicketMaximum = ApplicationTicketMaximumProvider.getMaximumFor(data);

            this.$patch({
                currency: data.priceList.currency,
                currencyOptions: data.currencyOptions,
                actionParams: this.getActionParams(data.configuration),
                actionUrl: this.getActionUrl(data.configuration),
                maxTickets: applicationTicketMaximum,
                fees: data.feeList ?? [],
            });


            if (data.priceList.priceLevels) {
                this.$patch({
                    priceLevels: PriceLevelStateCollectionFactory.buildFromPriceList(data.priceList, applicationTicketMaximum)
                });
            }

        },
        activatePriceLevel(state: PriceLevelState | undefined) {
            if (!state || state.active) {
                return;
            }

            this.$patch({
                priceLevels: PriceLevelStateCollectionProvider.getCollectionStateForActivated(state, this.priceLevels)
            });
        },
        activatePromotion(id: number) {
            this.$patch({
                priceLevels: PriceLevelStateCollectionProvider.updatedCollectionForPromotion(id, this.priceLevels)
            });
        },
        togglePriceLevelDisplay(state: PriceLevelState | undefined) {
            if (!state) {
                return;
            }

            if (state.alwaysOpen) {
                return false;
            }

            this.$patch({
                priceLevels: PriceLevelStateCollectionProvider.getCollectionStateForToggled(state, this.priceLevels)
            });
        },
        handleTicketTypeChange(payload: { state: TicketTypeState, stepper: StepperState }) {
            if (!this.activeHold) {
                return false;
            }

            const updatedState = PriceLevelStateCollectionProvider.provideUpdatedSubmitState(
                PriceLevelStateCollectionProvider.provideUpdatedCollectionForActiveTicketTypes(
                    HoldStateProvider.provideUpdatedTicketTypesFor(payload.state, payload.stepper, this.activeHold),
                    this.priceLevels
                )
            )

            this.$patch({
                priceLevels: updatedState
            });
        },
        handleSingleSelectionStepperChange(state: StepperState) {
            const updatedState = PriceLevelStateCollectionProvider.provideUpdatedSubmitState(
                PriceLevelStateCollectionProvider.provideCollectionForSingleSelectionStepperChange(state, this.priceLevels)
            );

            this.$patch({
                priceLevels: updatedState
            });
        },
        async submitSelection(data: {
            state: PriceLevelState | undefined,
            promotion: Promotion | null,
            promotionCode: string | null,
            eventName: string | undefined
        }) {
            if (!data.state) {
                return;
            }

            const recaptchaStore = useRecaptchaStore();
            const recaptchaRequired = recaptchaStore.isRequired();
            const state = data.state;

            if (!state.active) {
                this.activatePriceLevel(state);
                return;
            }

            if (!state.canSubmit) {
                return;
            }

            const types = PriceLevelStateProvider.getAddToCartTicketTypesUrlFragmentFor(state) ?? '';
            let url = location.origin + this.actionUrl + `?${types}&${this.actionParams}&currency=${state.currency}`;

            if (data.promotion) {
                url += `&promotionId=${encodeURIComponent(data.promotion.id!)}`;
            }

            if (data.promotion && data.promotion.isCodeRequired && data.promotionCode) {
                url += `&promotionCode=${encodeURIComponent(data.promotionCode)}`;
            }

            const eventId = Number.parseInt(new Url(url).getSearchParam('eventId')!);
            const analyticsTicketData = PriceLevelStateProvider.getAnalyticsCartTicketsFromBestSeat(state);
            const ticketItems: TicketItem[] = analyticsTicketData.ticketTypes.map(
                ticketType => GTMTicketItemFactory.fromTicketSelection(ticketType, 'bestseat', data.eventName ?? '-', `${eventId}`)
            );
            const ecommerceEvent: AnalyticsEcommerceEvent<CheckoutData<TicketItem>> = {
                event: AnalyticsEventName.AddToCart,
                ecommerce: {
                    currency: state.currency,
                    value: PriceLevelStateProvider.getSelectedTicketsValue(state),
                    items: ticketItems,
                }
            };

            if (recaptchaRequired) {
                recaptchaStore.queueSubmit(url, ecommerceEvent);
                recaptchaStore.instance().execute();

                return;
            }

            const analyticsStore = AnalyticsStorage.getInstance();

            FbqUtil.fbqEvent('AddToCart', [`${eventId}`], 'product');
            GTMUtils.pushGA4EcommerceToDataLayer(ecommerceEvent);

            if (analyticsStore) {
                analyticsStore.updateLocalStore({ currency: this.currency });
                analyticsStore.pushCartItems(ticketItems);
            }

            window.location.assign(url);
        }
    }
})
